import React, { useState } from "react";
import { Box, Button, Card, CardContent, TextField, Typography, IconButton, InputAdornment } from "@mui/material";
import axios from 'axios'; // For making API requests
import { useNavigate } from "react-router-dom";
import Colors from "../libs/Colors.js"; // Assuming you have a Colors file with colors
import { hosturl } from "../libs/Constant.js"; // Host URL for the backend API
import Visibility from '@mui/icons-material/Visibility'; // For password visibility icon
import VisibilityOff from '@mui/icons-material/VisibilityOff'; // For password visibility off icon

const ForgotPasswordPage = () => {
  const navigate = useNavigate();

  // State for email input, OTP input, password reset, and password visibility toggle
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState(""); // For new password input
  const [showPassword, setShowPassword] = useState(false); // For password visibility
  const [step, setStep] = useState(1); // To manage the flow: 1 for email input, 2 for OTP input, 3 for password reset
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Function to handle sending OTP
  const handleSendOtp = async () => {
    if (!email) {
      setErrorMessage("Please enter your email.");
      return;
    }

    try {
      setErrorMessage(""); // Clear any previous error messages
      setSuccessMessage(""); // Clear success message

      // Call the API to send the OTP
      const response = await axios.post(`${hosturl}/api/ebooster/auth/forgot-password`, { email });

      if (response.status === 200) {
        setSuccessMessage("OTP sent to your email.");
        setStep(2); // Move to the OTP verification step
      }
    } catch (error) {
      if (error.response) {
        setErrorMessage("An error occurred. Please try again.");
      } else {
        setErrorMessage("Network error. Please check your connection.");
      }
    }
  };

  // Function to handle OTP verification
  const handleVerifyOtp = async () => {
    if (!otp) {
      setErrorMessage("Please enter the OTP.");
      return;
    }

    try {
      setErrorMessage(""); // Clear any previous error messages
      setSuccessMessage(""); // Clear success message

      // Call the API to verify the OTP and reset the password
      const response = await axios.post(`${hosturl}/api/ebooster/auth/verify-code`, {
        email,
        verificationCode: otp,
        newPassword
      });

      if (response.status === 200) {
        setSuccessMessage("OTP verified successfully. You can now log in with your new password.");
        navigate("/"); // Navigate to login page after password reset
      }
    } catch (error) {
      if (error.response) {
        setErrorMessage("Invalid OTP.");
      } else {
        setErrorMessage("Network error. Please check your connection.");
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: "linear-gradient(to bottom, #44A08D, #E4DDEF)",
        padding: "20px",
      }}
    >
      <Card
        sx={{
          width: { xs: "100%", sm: "380px" },
          padding: "30px",
          boxShadow: "none",
          borderRadius: "16px",
          border: "1px solid #ddd",
        }}
      >
        <CardContent>
          <Typography
            variant="h5"
            align="center"
            gutterBottom
            fontWeight={500}
            sx={{ marginBottom: "20px", fontFamily:'poppins' }}
          >
            Forgot Password
          </Typography>

          {/* Step 1: Enter Email */}
          {step === 1 && (
            <>
              <TextField
                fullWidth
                label="Enter your email"
                margin="normal"
                variant="standard"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {errorMessage && (
                <Typography color="error" align="center" sx={{ marginTop: "10px" }}>
                  {errorMessage}
                </Typography>
              )}
              {successMessage && (
                <Typography color="success" align="center" sx={{ marginTop: "10px" }}>
                  {successMessage}
                </Typography>
              )}
              <Button
                fullWidth
                variant="contained"
                onClick={handleSendOtp}
                sx={{
                  marginTop: "20px",
                  background: Colors.buttonColor,
                  borderRadius: "8px",
                  textTransform: "none",fontFamily:'poppins'
                }}
              >
                Send OTP
              </Button>
            </>
          )}

          {/* Step 2: Enter OTP */}
          {step === 2 && (
            <>
              <TextField
                fullWidth
                label="Enter OTP"
                margin="normal"
                variant="standard"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
              {errorMessage && (
                <Typography color="error" align="center" sx={{ marginTop: "10px", fontFamily:'poppins', fontWeight:'500' }}>
                  {errorMessage}
                </Typography>
              )}
              {successMessage && (
                <Typography color="success" align="center" sx={{ marginTop: "10px" }}>
                  {successMessage}
                </Typography>
              )}
              <TextField
                fullWidth
                label="New Password"
                margin="normal"
                variant="standard"
                type={showPassword ? "text" : "password"} // Toggle password visibility
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)} // Toggle password visibility
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleVerifyOtp}
                sx={{
                  marginTop: "20px",
                  background: Colors.buttonColor,
                  borderRadius: "8px",
                  textTransform: "none",fontFamily:'poppins'
                }}
              >
                Reset Password
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ForgotPasswordPage;
